/**
 * Sending the bytes: one `PUT`, or the chunked loop when the server offered one.
 *
 * This is transport, not an API client. It lived in `admin-frontend/src/api/audio.ts`
 * as `uploadAudioDirect`, which meant the one piece of code that knows how to talk to
 * a signed object-store URL was filed under the router being deleted — and the
 * engagement frontend, which needs exactly the same thing, had its own copy that had
 * already drifted (no resumable branch, no Azure header).
 *
 * Nothing here knows what a recording is. It takes a URL and a `Blob`.
 */

import {
    canResume,
    uploadResumable,
    type ResumableUploadOffer,
} from './resumableUpload'

/**
 * A failed `PUT`, carrying the status that caused it.
 *
 * A bare `Error` with the status stringified into its message was enough while the only
 * consumer was a human reading a toast. It is not enough for a caller that has to *tell
 * the server* what went wrong: "the signed URL expired" and "the network dropped" are
 * different reasons with different fixes, and the difference is a status code — 403 with
 * `ExpiredToken` for the first, no status at all for the second.
 *
 * `status` is `null` precisely when the browser never got a response.
 */
export class DirectUploadError extends Error {
    readonly status: number | null

    constructor(message: string, status: number | null) {
        super(message)
        this.name = 'DirectUploadError'
        this.status = status
    }
}

export interface DirectUploadOptions {
    /** 0–100, fired as the bytes go out. */
    onProgress?: (percent: number) => void
    /** The resumable session, when the reserve response carried one. */
    offer?: ResumableUploadOffer | null
    /** Overrides the blob's own type. Rarely needed. */
    contentType?: string
}

/**
 * A URL is same-origin — and therefore ours — when it is a path rather than an absolute
 * URL. That distinction decides two things that must not be got wrong:
 *
 * - **Credentials.** Our own dev-fallback endpoint authenticates by cookie. A signed
 *   cloud URL must never receive one: the signature *is* the authorisation, and sending
 *   a session cookie to a storage provider leaks it outside our origin.
 * - **`x-ms-blob-type`.** Azure Blob Storage rejects a `PUT` without it. GCS and our own
 *   endpoint ignore it, but sending it unconditionally would be sending a
 *   provider-specific header to every provider.
 */
const isSameOrigin = (url: string): boolean => url.startsWith('/')

/**
 * Upload `file` to `url`, resuming in chunks if the server offered a session.
 *
 * Resolves when the bytes are stored. It does **not** commit the recording — that is a
 * separate call, deliberately, because the server owns the transition from "bytes
 * present" to "queued for transcription".
 */
export const uploadDirect = async (
    url: string,
    file: Blob,
    options: DirectUploadOptions = {}
): Promise<void> => {
    const { onProgress, offer, contentType } = options

    if (canResume(offer ?? undefined)) {
        return uploadResumable(offer!, file, {
            onProgress,
            withCredentials: isSameOrigin(offer!.url),
        })
    }

    const sameOrigin = isSameOrigin(url)
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', url)
        xhr.setRequestHeader(
            'Content-Type',
            contentType || file.type || 'application/octet-stream'
        )
        if (sameOrigin) {
            xhr.withCredentials = true
        } else {
            xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob')
        }

        if (onProgress) {
            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    onProgress(Math.round((event.loaded / event.total) * 100))
                }
            }
        }

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve()
            } else {
                reject(
                    new DirectUploadError(
                        `Upload failed with status ${xhr.status}: ${xhr.responseText}`,
                        xhr.status
                    )
                )
            }
        }
        xhr.onerror = () =>
            reject(new DirectUploadError('Network error during upload', null))
        xhr.send(file)
    })
}
